import React, { useEffect, useRef } from "react";
import styles from "./styles.module.css";
import { Link } from "react-router-dom";
import {
  useSiteAssets,
  useSiteAssetsLoaded,
  optimized,
} from "../../../siteAssets";
import heroFallback from "../../../assets/home.png";

// Seconds spent dissolving at each end of the video
const DISSOLVE = 1.8;
// How long the hero image shows on its own before/between video plays
const IMAGE_HOLD_MS = 4000;

const Hero = () => {
  const assets = useSiteAssets();
  const assetsLoaded = useSiteAssetsLoaded();
  const videoRef = useRef(null);

  // API image is the source of truth; the bundled photo only appears when
  // the API answered and no custom hero is set. Until then: dark brand base.
  const heroImage = assetsLoaded
    ? assets.hero
      ? optimized(assets.hero)
      : heroFallback
    : null;

  // Breathing cycle: hero image alone for a few seconds → video dissolves
  // in → plays → dissolves out → image alone again → repeat. If the browser
  // blocks playback entirely, the image simply stays — a graceful fallback.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let frame;
    let holdTimer;
    video.style.opacity = 0;

    const startPlayback = () => {
      video.play().catch(() => {}); // muted play is allowed without a gesture
    };

    const handleEnded = () => {
      video.style.opacity = 0;
      video.currentTime = 0;
      holdTimer = setTimeout(startPlayback, IMAGE_HOLD_MS);
    };
    video.addEventListener("ended", handleEnded);

    const tick = () => {
      if (video.duration && !video.paused && !video.ended) {
        const t = video.currentTime;
        const remaining = video.duration - t;
        const opacity = Math.max(0, Math.min(1, t / DISSOLVE, remaining / DISSOLVE));
        video.style.opacity = opacity.toFixed(3);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    holdTimer = setTimeout(startPlayback, IMAGE_HOLD_MS);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(holdTimer);
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  return (
    <section
      className={styles.heroCon}
      style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
    >
      <video
        ref={videoRef}
        className={styles.heroVideo}
        src={`${process.env.PUBLIC_URL}/videos/hero-loop-1080.mp4`}
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <div className={styles.overlay} />
      <div className={styles.hero}>
        <p className={styles.eyebrow}>Lormami — Future Wear</p>
        <h1 className={styles.future}>
          The Future
          <br />
          Is Here<span className={styles.now}>, Now</span>
        </h1>
        <p className={styles.welcome}>
          Future-facing fashion for those who move first. Considered pieces,
          built to last.
        </p>
        <div className={styles.actions}>
          <Link to="/new-arrivals" className={styles.btnPrimary}>
            Shop Now
          </Link>
          {/* scrolls to the community registration section further down */}
          <a href="#preorder" className={styles.btnGhost}>
            Join the Community
          </a>
        </div>
      </div>
      <span className={styles.scrollHint}>Scroll</span>
    </section>
  );
};

export default Hero;
